import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Row, Col, Card, Table, Button, Input, Select, Drawer,
  Form, notification, Space, Typography, Tag, Tooltip,
  Popconfirm, InputNumber, DatePicker, Avatar, Divider, Modal, Slider,
  Tabs, Badge,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  ExclamationCircleOutlined, UserOutlined, PhoneOutlined,
  ShopOutlined, EnvironmentOutlined, CameraOutlined, LoadingOutlined,
  CalendarOutlined, MailOutlined, GlobalOutlined, EyeOutlined,
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
  BankOutlined, ScissorOutlined, ShareAltOutlined, CopyOutlined, WhatsAppOutlined, LinkOutlined,
} from "@ant-design/icons";
import Cropper from "react-easy-crop";
import axios from "axios";
import moment from "moment";
import { baseUrl } from "../../config";

// ── Public form URLs ──────────────────────────────────────────────────────────
const getFormUrl   = () => `${window.location.origin}/business-directory-form`;
const getUpdateUrl = () => `${window.location.origin}/business-directory-update`;

const WHATSAPP_MSG = (url) =>
`🙏 *नमस्कार — ABRSVS व्यवसाय निर्देशिका*

अखिल भारतीय राजपुरोहित समाज विकास संस्थान की *व्यवसाय निर्देशिका* में अपना व्यवसाय / नौकरी की जानकारी दर्ज करें।

इससे समाज के लोग आपका व्यवसाय आसानी से ढूंढ सकेंगे और एक-दूसरे से जुड़ सकेंगे।

👉 *नया फॉर्म भरें:*
${url}

✏️ *पहले से दर्ज जानकारी अपडेट करें:*
${getUpdateUrl()}`;

// ── Share Form Modal ──────────────────────────────────────────────────────────
function ShareFormButton() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = getFormUrl();

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      notification.error({ message: 'Copy failed', placement: 'topRight' });
    }
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(WHATSAPP_MSG(url))}`, '_blank');
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'ABRSVS व्यवसाय निर्देशिका',
          text: 'समाज की व्यवसाय निर्देशिका में अपना व्यवसाय दर्ज करें।',
          url,
        });
      } catch {}
    } else {
      shareWhatsApp();
    }
  };

  return (
    <>
      <Button icon={<ShareAltOutlined />} onClick={() => setOpen(true)}
        style={{ background: '#25D366', borderColor: '#25D366', color: '#fff', borderRadius: 8, fontWeight: 600 }}>
        Share Form
      </Button>

      <Modal open={open} onCancel={() => setOpen(false)} footer={null} centered
        width={440} destroyOnClose
        title={
          <span style={{ fontWeight: 700, color: '#7a0000', fontSize: 15 }}>
            <ShareAltOutlined style={{ marginRight: 8, color: '#25D366' }} />
            व्यवसाय फॉर्म शेयर करें
          </span>
        }>

        {/* URL box */}
        <div style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 8,
          padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <LinkOutlined style={{ color: '#7a0000', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 12, color: '#333', wordBreak: 'break-all' }}>{url}</span>
          <Button size="small" icon={<CopyOutlined />} onClick={copyLink}
            style={{ flexShrink: 0, borderColor: copied ? '#52c41a' : '#7a0000',
              color: copied ? '#52c41a' : '#7a0000', borderRadius: 6, fontWeight: 600 }}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>

        {/* Preview message */}
        <div style={{ background: '#e7f8ee', border: '1px solid #b7ebc8', borderRadius: 8,
          padding: '12px 14px', marginBottom: 20, fontSize: 12, color: '#1a5c2e',
          whiteSpace: 'pre-line', lineHeight: 1.7 }}>
          {WHATSAPP_MSG(url)}
        </div>

        {/* Share buttons */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button block icon={<WhatsAppOutlined />} onClick={shareWhatsApp}
            style={{ background: '#25D366', borderColor: '#25D366', color: '#fff',
              borderRadius: 8, fontWeight: 700, height: 44, flex: 1 }}>
            WhatsApp पर शेयर करें
          </Button>
          <Button block icon={<ShareAltOutlined />} onClick={shareNative}
            style={{ borderColor: '#7a0000', color: '#7a0000',
              borderRadius: 8, fontWeight: 600, height: 44, flex: 1 }}>
            Other Apps
          </Button>
        </div>

        <div style={{ marginTop: 14, fontSize: 11, color: '#aaa', textAlign: 'center' }}>
          इस लिंक को WhatsApp groups, SMS या social media पर शेयर करें
        </div>
      </Modal>
    </>
  );
}

// ── Canvas crop helpers ───────────────────────────────────────────────────────
const createImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.addEventListener('load', () => resolve(img));
  img.addEventListener('error', reject);
  img.setAttribute('crossOrigin', 'anonymous');
  img.src = url;
});

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image  = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx    = canvas.getContext('2d');
  const maxDim = 900;
  const scale  = Math.min(maxDim / pixelCrop.width, maxDim / pixelCrop.height, 1);
  canvas.width  = Math.round(pixelCrop.width  * scale);
  canvas.height = Math.round(pixelCrop.height * scale);
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, canvas.width, canvas.height);
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
}

async function doUploadBlob(blob, type) {
  const fd = new FormData();
  fd.append('photo', blob, 'photo.jpg');
  const res  = await fetch(`${baseUrl}/admin/business-directory/upload-photo?type=${type}`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }, body: fd });
  const data = await res.json();
  if (!data.status) throw new Error(data.message || 'Upload failed');
  return data.url;
}

// ── Shared Crop Modal ─────────────────────────────────────────────────────────
function CropModal({ open, imageSrc, aspect, onDone, onCancel }) {
  const [crop, setCrop]           = useState({ x: 0, y: 0 });
  const [zoom, setZoom]           = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [applying, setApplying]   = useState(false);
  const onCropComplete = useCallback((_, pixels) => setCroppedPixels(pixels), []);

  const apply = async () => {
    if (!croppedPixels) return;
    setApplying(true);
    try { onDone(await getCroppedBlob(imageSrc, croppedPixels)); }
    catch { notification.error({ message: 'Crop विफल हुआ।', placement: 'top' }); }
    finally { setApplying(false); }
  };

  return (
    <Modal open={open} onCancel={onCancel} width={360} centered destroyOnClose
      title={<span><ScissorOutlined style={{ marginRight: 8, color: '#7a0000' }} />फोटो काटें और सेट करें</span>}
      footer={[
        <Button key="c" onClick={onCancel}>रद्द करें</Button>,
        <Button key="a" type="primary" loading={applying} onClick={apply}
          style={{ background: '#7a0000', borderColor: '#7a0000' }}>✓ सेट करें</Button>,
      ]}>
      <div style={{ position: 'relative', height: 280, background: '#111', borderRadius: 8, overflow: 'hidden' }}>
        {imageSrc && <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={aspect}
          onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />}
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>ज़ूम करें</div>
        <Slider min={1} max={3} step={0.05} value={zoom} onChange={setZoom}
          trackStyle={{ background: '#7a0000' }} handleStyle={{ borderColor: '#7a0000' }} />
      </div>
      <div style={{ fontSize: 11, color: '#aaa', marginTop: 6, textAlign: 'center' }}>
        खींचकर और ज़ूम करके सही जगह सेट करें
      </div>
    </Modal>
  );
}

// ── Admin profile photo (square, with crop) ───────────────────────────────────
const AdminPhotoUpload = ({ value, onChange }) => {
  const [preview, setPreview]     = useState(value || null);
  const [uploading, setUploading] = useState(false);
  const [rawSrc, setRawSrc]       = useState(null);
  const [cropOpen, setCropOpen]   = useState(false);
  const inputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      notification.warning({ message: 'JPG या PNG फोटो ही चुनें।', placement: 'top' }); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onCropDone = async (blob) => {
    setCropOpen(false);
    setUploading(true);
    try {
      const url = await doUploadBlob(blob, 'profile');
      setPreview(url); onChange(url);
    } catch (err) {
      notification.error({ message: 'अपलोड विफल: ' + err.message, placement: 'top' });
    } finally { setUploading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files[0])} />
      <div style={{ position: 'relative', width: 90, height: 90 }}>
        <div onClick={() => !uploading && inputRef.current?.click()}
          style={{ width: 90, height: 90, borderRadius: 10, border: '3px solid #7a0000',
            overflow: 'hidden', cursor: 'pointer', background: '#f0f2f5', position: 'relative' }}>
          {preview
            ? <img src={preview} alt="profile"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                {uploading
                  ? <LoadingOutlined style={{ fontSize: 28, color: '#7a0000' }} />
                  : <UserOutlined style={{ fontSize: 36, color: '#ccc' }} />
                }
              </div>
          }
        </div>
        <div onClick={() => !uploading && inputRef.current?.click()}
          style={{ position: 'absolute', bottom: -4, right: -4, width: 26, height: 26,
            borderRadius: 8, background: '#7a0000', border: '2px solid #fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <CameraOutlined style={{ color: '#fff', fontSize: 12 }} />
        </div>
        {preview && !uploading && (
          <button type="button"
            onClick={e => { e.stopPropagation(); setPreview(null); onChange(null); if (inputRef.current) inputRef.current.value = ''; }}
            style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20,
              borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff',
              color: '#fff', fontSize: 10, cursor: 'pointer', lineHeight: '16px',
              textAlign: 'center', padding: 0 }}>✕</button>
        )}
      </div>
      <span style={{ fontSize: 10, color: '#aaa', marginTop: 6 }}>
        {preview ? 'बदलने के लिए क्लिक करें' : 'फोटो · max 5MB'}
      </span>
      <CropModal open={cropOpen} imageSrc={rawSrc} aspect={1}
        onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </div>
  );
};

// ── Admin business photos (2 slots, with crop) ────────────────────────────────
const AdminMultiPhotoUpload = ({ value, onChange }) => {
  const [slots, setSlots]       = useState(() => { const u = value ? value.split(',').filter(Boolean) : []; return [u[0]||null, u[1]||null]; });
  const [uploading, setUploading] = useState([false, false]);
  const [rawSrc, setRawSrc]     = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingIdx, setPendingIdx] = useState(null);
  const refs = [useRef(null), useRef(null)];

  const notify = (s) => onChange(s.filter(Boolean).join(',') || null);

  const handleFile = (file, idx) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      notification.warning({ message: 'JPG या PNG फोटो ही चुनें।', placement: 'top' }); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setPendingIdx(idx); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (refs[idx].current) refs[idx].current.value = '';
  };

  const onCropDone = async (blob) => {
    setCropOpen(false);
    const idx = pendingIdx;
    setUploading(u => { const n=[...u]; n[idx]=true; return n; });
    try {
      const url = await doUploadBlob(blob, 'business');
      setSlots(s => { const n=[...s]; n[idx]=url; notify(n); return n; });
    } catch (err) {
      notification.error({ message: 'अपलोड विफल: ' + err.message, placement: 'top' });
    } finally { setUploading(u => { const n=[...u]; n[idx]=false; return n; }); }
  };

  const remove = (idx) => {
    setSlots(s => { const n=[...s]; n[idx]=null; notify(n); return n; });
    if (refs[idx].current) refs[idx].current.value = '';
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
        border: '2px dashed #7a0000', borderRadius: 10, padding: '10px 12px',
        background: '#f8faff', minHeight: 96 }}>
        {[0,1].map(idx => (
          <input key={idx} ref={refs[idx]} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0], idx)} />
        ))}
        {[0,1].map(idx => (
          slots[idx] ? (
            <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
              <img src={slots[idx]} alt={`biz-${idx+1}`} onClick={() => refs[idx].current?.click()}
                style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover',
                  border: '2px solid #7a0000', cursor: 'pointer', display: 'block' }} />
              <div onClick={() => refs[idx].current?.click()}
                style={{ position: 'absolute', inset: 0, borderRadius: 8,
                  background: 'rgba(0,0,0,0.22)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', cursor: 'pointer' }}>
                <CameraOutlined style={{ color: '#fff', fontSize: 16 }} />
              </div>
              <button type="button" onClick={() => remove(idx)}
                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18,
                  borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff',
                  color: '#fff', fontSize: 9, cursor: 'pointer', lineHeight: '14px',
                  textAlign: 'center', padding: 0 }}>✕</button>
            </div>
          ) : uploading[idx] ? (
            <div key={idx} style={{ width: 80, height: 80, borderRadius: 8,
              border: '2px dashed #7a0000', background: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LoadingOutlined style={{ fontSize: 20, color: '#7a0000' }} />
            </div>
          ) : (
            <button key={idx} type="button" onClick={() => refs[idx].current?.click()}
              style={{ width: 80, height: 80, borderRadius: 8, border: '2px dashed #7a0000',
                background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0 }}>
              <PlusOutlined style={{ fontSize: 20, color: '#7a0000' }} />
              <span style={{ fontSize: 10, color: '#7a0000' }}>फोटो {idx+1}</span>
            </button>
          )
        ))}
        <div style={{ flex: 1, color: '#bbb', fontSize: 11, lineHeight: 1.8, minWidth: 80 }}>
          <CameraOutlined style={{ fontSize: 16, color: '#7a0000', display: 'block', marginBottom: 4 }} />
          दुकान की फोटो<br />JPG / PNG<br />max 5MB
        </div>
      </div>
      <CropModal open={cropOpen} imageSrc={rawSrc} aspect={4/3}
        onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </>
  );
};

const { Text } = Typography;

// ── Validation helpers ────────────────────────────────────────────────────────
const URL_PATTERN  = /(https?:\/\/|www\.|\.com|\.net|\.org|\.in|\.co|<[^>]+>|javascript:|on\w+=|&lt;|&#|<script|SELECT |INSERT |DROP |DELETE |UPDATE )/i;
const SAFE_TEXT    = /^[a-zA-Z0-9ऀ-ॿ\s.,'\-/()&@!?:;​‌‍]+$/;
const NAME_PATTERN = /^[a-zA-Zऀ-ॿ\s.''\-]+$/;

const validateSafe = (label) => (_, value) => {
  if (!value || !value.trim()) return Promise.resolve();
  if (URL_PATTERN.test(value)) return Promise.reject(`${label} में link, script या HTML की अनुमति नहीं है।`);
  return Promise.resolve();
};

const validateName = (label) => (_, value) => {
  if (!value || !value.trim()) return Promise.reject(`${label} दर्ज करें।`);
  if (URL_PATTERN.test(value)) return Promise.reject('Link या script की अनुमति नहीं है।');
  if (!NAME_PATTERN.test(value.trim())) return Promise.reject(`${label} में केवल अक्षर (A-Z / हिंदी) लिखें।`);
  return Promise.resolve();
};

const phoneRules = [
  { required: true, message: 'मोबाइल नंबर आवश्यक है।' },
  {
    validator: (_, value) => {
      if (!value) return Promise.reject('मोबाइल नंबर दर्ज करें।');
      if (!/^\d+$/.test(value)) return Promise.reject('केवल अंक (0-9) दर्ज करें।');
      if (value.length !== 10) return Promise.reject('मोबाइल नंबर 10 अंकों का होना चाहिए।');
      if (!/^[6-9]/.test(value)) return Promise.reject('मोबाइल नंबर 6, 7, 8 या 9 से शुरू होना चाहिए।');
      return Promise.resolve();
    },
  },
];
const { Option } = Select;
const { TextArea } = Input;
const { Item } = Form;

const STATUS_COLORS  = { Pending: 'orange', Verified: 'green', Rejected: 'red' };
const STATUS_ICONS   = {
  Verified: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
  Rejected: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
  Pending:  <ClockCircleOutlined style={{ color: '#fa8c16' }} />,
};

// ── Detail view rendered inside the View drawer ───────────────────────────────
function DetailView({ r }) {
  const businessPhotos = r.business_photo
    ? r.business_photo.split(',').filter(Boolean)
    : [];

  const infoRow = (icon, label, value) => value ? (
    <div style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid #f5f5f5' }}>
      <span style={{ color: '#999', width: 20, flexShrink: 0, paddingTop: 1 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: '#aaa', lineHeight: 1.2 }}>{label}</div>
        <div style={{ fontSize: 13, color: '#1a1a1a', fontWeight: 500 }}>{value}</div>
      </div>
    </div>
  ) : null;

  return (
    <div style={{ paddingBottom: 16 }}>

      {/* ── Header: photo + name + status ── */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 20,
        background: 'linear-gradient(135deg, #7a0000 0%, #2a4a6b 100%)',
        borderRadius: 12, padding: '18px 16px', color: '#fff' }}>
        <Avatar
          src={r.profile_photo || undefined}
          icon={!r.profile_photo && <UserOutlined />}
          size={80}
          shape="square"
          style={{ borderRadius: 10, border: '3px solid #d4a84b', flexShrink: 0,
            background: r.profile_photo ? undefined : '#2a4a6b' }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>{r.name}</div>
          {r.father_name && <div style={{ fontSize: 13, color: '#d4a84b', marginTop: 2 }}>S/o {r.father_name}</div>}
          {r.gotra && <div style={{ fontSize: 12, color: '#b0c4de', marginTop: 2 }}>गोत्र: {r.gotra}</div>}
          <div style={{ marginTop: 8 }}>
            <Tag color={STATUS_COLORS[r.verification_status] || 'default'}
              icon={STATUS_ICONS[r.verification_status] || STATUS_ICONS.Pending}
              style={{ fontWeight: 600, fontSize: 12 }}>
              {r.verification_status || 'Pending'}
            </Tag>
            {r.sector_label && <Tag color="blue" style={{ fontSize: 12 }}>{r.sector_label}</Tag>}
          </div>
        </div>
      </div>

      {/* ── Business photos ── */}
      {businessPhotos.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', letterSpacing: '1px',
            textTransform: 'uppercase', marginBottom: 8 }}>दुकान / व्यवसाय की फोटो</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {businessPhotos.map((url, i) => (
              <img key={i} src={url} alt={`business-${i + 1}`}
                style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8,
                  border: '2px solid #d4a84b', cursor: 'pointer' }}
                onClick={() => window.open(url, '_blank')} />
            ))}
          </div>
        </div>
      )}

      {/* ── Contact ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', letterSpacing: '1px',
        textTransform: 'uppercase', margin: '12px 0 4px' }}>संपर्क जानकारी</div>
      {infoRow(<PhoneOutlined />, 'WhatsApp / मोबाइल', r.phone_no)}
      {infoRow(<PhoneOutlined />, 'वैकल्पिक नंबर', r.alternate_phone)}
      {infoRow(<MailOutlined />, 'ईमेल', r.email)}

      {/* ── Business ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', letterSpacing: '1px',
        textTransform: 'uppercase', margin: '14px 0 4px' }}>व्यवसाय</div>
      {infoRow(<ShopOutlined />, 'फर्म / दुकान', r.firm_name)}
      {infoRow(<BankOutlined />, 'मुख्य क्षेत्र', [r.sector_label, r.sub_sector_label].filter(Boolean).join(' › '))}
      {infoRow(<CalendarOutlined />, 'स्थापना वर्ष', r.establishment_year ? String(r.establishment_year) : null)}
      {infoRow('🏅', 'अनुभव (वर्ष)', r.experience_years ? `${r.experience_years} वर्ष` : null)}
      {infoRow('⭐', 'रैंक / पदवी', r.biz_rank)}
      {infoRow('💼', 'पद / Post', r.post)}
      {r.business_about && (
        <div style={{ padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
          <div style={{ fontSize: 11, color: '#aaa', lineHeight: 1.2, marginBottom: 4 }}>व्यवसाय के बारे में</div>
          <div style={{ fontSize: 13, color: '#1a1a1a', lineHeight: 1.7, background: '#f9f9f9',
            borderRadius: 6, padding: '6px 10px', borderLeft: '3px solid #d4a84b' }}>
            {r.business_about}
          </div>
        </div>
      )}
      {infoRow(<EnvironmentOutlined />, 'व्यावसायिक पता', r.business_address)}

      {/* ── Business Location ── */}
      {(r.current_city || r.state) && <>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', letterSpacing: '1px',
          textTransform: 'uppercase', margin: '14px 0 4px' }}>व्यवसाय का शहर</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
          borderBottom: '1px solid #f5f5f5' }}>
          <EnvironmentOutlined style={{ color: '#d4a84b', fontSize: 16 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>
              {[r.current_city, r.state].filter(Boolean).join(', ')}
            </div>
          </div>
        </div>
      </>}

      {/* ── Native Location ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', letterSpacing: '1px',
        textTransform: 'uppercase', margin: '14px 0 4px' }}>मूल स्थान</div>
      {infoRow(<EnvironmentOutlined />, 'गाँव / तहसील / जिला',
        [r.village, r.tehsil, r.district].filter(Boolean).join(', '))}

      {/* ── Personal ── */}
      {(r.dob || r.gotra) && <>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', letterSpacing: '1px',
          textTransform: 'uppercase', margin: '14px 0 4px' }}>व्यक्तिगत</div>
        {infoRow(<CalendarOutlined />, 'जन्म तिथि',
          r.dob && r.dob !== '0000-00-00' ? r.dob : null)}
        {infoRow(<UserOutlined />, 'गोत्र', r.gotra)}
      </>}

      {/* ── Admin notes ── */}
      {r.admin_notes && <>
        <Divider style={{ margin: '14px 0 10px' }} />
        <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', letterSpacing: '1px',
          textTransform: 'uppercase', marginBottom: 6 }}>Admin Notes</div>
        <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6,
          padding: '8px 12px', fontSize: 13, color: '#614700' }}>
          {r.admin_notes}
        </div>
      </>}

      {/* ── Submitted at ── */}
      {r.created_at && (
        <div style={{ marginTop: 14, fontSize: 11, color: '#bbb', textAlign: 'right' }}>
          Submitted: {new Date(r.created_at).toLocaleString('en-IN')}
        </div>
      )}
    </div>
  );
}

function SubmissionList() {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [sectors, setSectors]     = useState([]);
  const [search, setSearch]       = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCity, setFilterCity]     = useState('');
  const [filterState, setFilterState]   = useState('');
  const [showFilters, setShowFilters]   = useState(false);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);

  // Tabs
  const [activeTab, setActiveTab] = useState('submissions');

  // Update requests
  const [updateRequests, setUpdateRequests]       = useState([]);
  const [loadingRequests, setLoadingRequests]     = useState(false);
  const [updateRequestCount, setUpdateRequestCount] = useState(0);
  const [diffModal, setDiffModal]                 = useState(null); // {old_data, new_data, id, name}
  const [approving, setApproving]                 = useState(null); // id being approved
  const [rejecting, setRejecting]                 = useState(null); // id being rejected

  // Edit drawer state
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [saving, setSaving]           = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);
  const [form] = Form.useForm();

  // View detail drawer state
  const [viewOpen, setViewOpen]       = useState(false);
  const [viewRecord, setViewRecord]   = useState(null);

  const subOptions = selectedSector
    ? (sectors.find(s => String(s.id) === String(selectedSector))?.sub_sectors || [])
    : [];

  useEffect(() => { fetchSectors(); }, []);
  useEffect(() => { fetchData(); }, [search, filterSector, filterStatus, filterCity, filterState, page]);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

  const fetchSectors = async () => {
    try {
      const res = await axios.get(`${baseUrl}/business-sector/list`, { headers: authHeader() });
      if (res.data.status) setSectors(res.data.result || []);
    } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search)        params.search              = search;
      if (filterSector)  params.sector_id           = filterSector;
      if (filterStatus)  params.verification_status = filterStatus;
      if (filterCity)    params.city                = filterCity;
      if (filterState)   params.state               = filterState;
      const res = await axios.get(`${baseUrl}/business-directory/list`, { headers: authHeader(), params });
      if (res.data.status) {
        setData((res.data.result || []).map((r, i) => ({ ...r, key: r.id || i })));
        setTotal(res.data.totalCount || 0);
      }
    } catch {
      notification.error({ message: 'Error', description: 'Could not load submissions', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  };

  // ── Update requests ──────────────────────────────────────────────────────────
  const fetchUpdateRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await axios.get(`${baseUrl}/business-directory/update-requests`, { headers: authHeader() });
      if (res.data.status) {
        const list = (res.data.result || []).map((r, i) => ({ ...r, key: r.id || i }));
        setUpdateRequests(list);
        setUpdateRequestCount(list.length);
      }
    } catch {
      notification.error({ message: 'Update requests load failed', placement: 'topRight' });
    } finally { setLoadingRequests(false); }
  };

  const approveRequest = async (id) => {
    setApproving(id);
    try {
      await axios.post(`${baseUrl}/business-directory/update-requests/${id}/approve`, {}, { headers: authHeader() });
      notification.success({ message: 'Approved', description: 'Update applied to the directory.', placement: 'topRight' });
      setDiffModal(null);
      fetchUpdateRequests();
      fetchData();
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Approval failed', placement: 'topRight' });
    } finally { setApproving(null); }
  };

  const rejectRequest = async (id) => {
    setRejecting(id);
    try {
      await axios.post(`${baseUrl}/business-directory/update-requests/${id}/reject`, {}, { headers: authHeader() });
      notification.success({ message: 'Rejected', description: 'Update request discarded. Old data retained.', placement: 'topRight' });
      setDiffModal(null);
      fetchUpdateRequests();
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Rejection failed', placement: 'topRight' });
    } finally { setRejecting(null); }
  };

  // ── Open drawer ─────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setSelectedSector(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setSelectedSector(record.sector_id ? String(record.sector_id) : null);
    form.setFieldsValue({
      name:               record.name,
      father_name:        record.father_name,
      phone_no:           record.phone_no,
      sector:             record.sector_id ? String(record.sector_id) : undefined,
      sub_sector:         record.sub_sector_id ? String(record.sub_sector_id) : undefined,
      firm_name:          record.firm_name,
      establishment_year: record.establishment_year,
      business_about:     record.business_about,
      business_address:   record.business_address,
      village:            record.village,
      tehsil:             record.tehsil,
      district:           record.district,
      dob:                record.dob && record.dob !== '0000-00-00' ? moment(record.dob) : null,
      gotra:              record.gotra,
      email:              record.email,
      alternate_phone:    record.alternate_phone,
      current_city:       record.current_city,
      state:              record.state,
      profile_photo:      record.profile_photo,
      business_photo:     record.business_photo,
      experience_years:   record.experience_years,
      biz_rank:           record.biz_rank,
      post:               record.post,
      verification_status: record.verification_status,
      admin_notes:        record.admin_notes,
    });
    setDrawerOpen(true);
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const sectorLabel  = sectors.find(s => String(s.id) === String(values.sector))?.name || '';
      const sectorObj    = sectors.find(s => String(s.id) === String(values.sector));
      const subLabel     = (sectorObj?.sub_sectors || []).find(s => String(s.id) === String(values.sub_sector))?.name || '';

      const payload = {
        name:               values.name,
        father_name:        values.father_name || null,
        phone_no:           values.phone_no,
        sector_id:          values.sector || null,
        sector_label:       sectorLabel,
        sub_sector_id:      values.sub_sector || null,
        sub_sector_label:   subLabel,
        firm_name:          values.firm_name || null,
        establishment_year: values.establishment_year || null,
        business_about:     values.business_about || null,
        business_address:   values.business_address || null,
        village:            values.village || null,
        tehsil:             values.tehsil || null,
        district:           values.district || null,
        dob:                values.dob ? moment(values.dob).format('YYYY-MM-DD') : null,
        gotra:              values.gotra || null,
        email:              values.email || null,
        alternate_phone:    values.alternate_phone || null,
        current_city:       values.current_city || null,
        state:              values.state || null,
        profile_photo:      values.profile_photo || null,
        business_photo:     values.business_photo || null,
        experience_years:   values.experience_years || null,
        biz_rank:           values.biz_rank || null,
        post:               values.post || null,
        verification_status: values.verification_status || 'Pending',
        admin_notes:        values.admin_notes || null,
      };

      if (editingId) {
        await axios.patch(`${baseUrl}/business-directory/update/${editingId}`, payload, { headers: authHeader() });
        notification.success({ message: 'Updated', description: 'Entry updated successfully', placement: 'topRight' });
      } else {
        await axios.post(`${baseUrl}/business-directory/public-submit`, payload);
        notification.success({ message: 'Added', description: 'Entry added successfully', placement: 'topRight' });
      }
      setDrawerOpen(false);
      fetchData();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Save failed', placement: 'topRight' });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const onDelete = async (id) => {
    try {
      await axios.delete(`${baseUrl}/business-directory/delete/${id}`, { headers: authHeader() });
      notification.success({ message: 'Deleted', description: 'Entry deleted', placement: 'topRight' });
      fetchData();
    } catch (err) {
      notification.error({ message: 'Error', description: err?.response?.data?.message || 'Delete failed', placement: 'topRight' });
    }
  };

  // ── Columns ─────────────────────────────────────────────────────────────────
  const columns = [
    { title: '#', dataIndex: 'id', key: 'id', width: 52 },
    {
      title: 'Photo',
      key: 'photo',
      width: 68,
      render: (_, r) => (
        <Avatar
          src={r.profile_photo || undefined}
          icon={!r.profile_photo && <UserOutlined />}
          size={46}
          shape="square"
          style={{
            borderRadius: 8,
            border: r.profile_photo ? '2px solid #d4a84b' : '2px solid #e8e8e8',
            background: r.profile_photo ? undefined : '#f5f5f5',
            cursor: 'pointer',
          }}
          onClick={() => { setViewRecord(r); setViewOpen(true); }}
        />
      ),
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, r) => (
        <div style={{ cursor: 'pointer' }} onClick={() => { setViewRecord(r); setViewOpen(true); }}>
          <Text strong style={{ fontSize: 13 }}>{r.name}</Text>
          {r.father_name && <><br /><Text type="secondary" style={{ fontSize: 11 }}>S/o {r.father_name}</Text></>}
          {r.gotra && <><br /><Text type="secondary" style={{ fontSize: 11 }}>गोत्र: {r.gotra}</Text></>}
        </div>
      ),
    },
    {
      title: 'Phone',
      key: 'phone',
      width: 130,
      render: (_, r) => (
        <div>
          <Text style={{ fontSize: 13 }}>{r.phone_no}</Text>
          {r.alternate_phone && <><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.alternate_phone}</Text></>}
        </div>
      ),
    },
    {
      title: 'Sector',
      key: 'sector',
      render: (_, r) => (
        <div>
          {r.sector_label || r.sector_name
            ? <Tag color="blue" style={{ fontSize: 11, marginBottom: 2 }}>{r.sector_label || r.sector_name}</Tag>
            : <Text type="secondary">—</Text>
          }
          {r.sub_sector_label && <><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.sub_sector_label}</Text></>}
        </div>
      ),
    },
    {
      title: 'Firm / Shop',
      key: 'firm',
      render: (_, r) => (
        <div>
          <Text style={{ fontSize: 13 }}>{r.firm_name || '—'}</Text>
          {r.establishment_year && <><br /><Text type="secondary" style={{ fontSize: 11 }}>Est. {r.establishment_year}</Text></>}
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, r) => (
        <Text style={{ fontSize: 12 }}>
          {[r.village, r.tehsil, r.district].filter(Boolean).join(', ') || '—'}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'verification_status',
      key: 'status',
      width: 100,
      render: v => <Tag color={STATUS_COLORS[v] || 'default'}>{v || 'Pending'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size={6} onClick={e => e.stopPropagation()}>
          <Button size="small"
            onClick={() => { setViewRecord(record); setViewOpen(true); }}
            style={{ borderColor: '#d4a84b', color: '#d4a84b', borderRadius: 8, fontWeight: 600, minWidth: 54 }}>
            View
          </Button>
          <Button size="small"
            onClick={() => openEdit(record)}
            style={{ borderColor: '#7a0000', color: '#7a0000', borderRadius: 8, fontWeight: 600, minWidth: 54 }}>
            Edit
          </Button>
          <Tooltip title="Send Update Link on WhatsApp">
            <Button size="small" icon={<WhatsAppOutlined />}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await axios.post(`${baseUrl}/business-directory/${record.id}/send-update-link`, {}, { headers: authHeader() });
                  notification.success({ message: 'Update link sent!', description: `WhatsApp link sent to ${record.phone_no}`, placement: 'topRight' });
                } catch (err) {
                  notification.error({ message: err?.response?.data?.message || 'Failed to send link', placement: 'topRight' });
                }
              }}
              style={{ background: '#25D366', borderColor: '#25D366', color: '#fff', borderRadius: 8 }}
            />
          </Tooltip>
          <Popconfirm
            title="इस entry को हटाएं?"
            onConfirm={() => onDelete(record.id)}
            okText="हाँ, हटाएं" cancelText="रद्द करें" okType="danger"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Button size="small" danger style={{ borderRadius: 8, fontWeight: 600, minWidth: 64 }}>
              Remove
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Form fields ──────────────────────────────────────────────────────────────
  const renderFormFields = () => (
    <>
      {/* Personal */}
      <div style={sectionStyle}>Personal Info (व्यक्तिगत)</div>
      <Row gutter={[16, 0]}>
        {/* Name + Father */}
        <Col xs={24} sm={12}>
          <Item label="Full Name (पूरा नाम)" name="name"
            rules={[{ required: true }, { validator: validateName('पूरा नाम') }]}>
            <Input prefix={<UserOutlined />} placeholder="पूरा नाम" maxLength={100} />
          </Item>
        </Col>
        <Col xs={24} sm={12}>
          <Item label="Father's Name (पिता का नाम)" name="father_name"
            rules={[{ validator: validateName('पिता का नाम') }]}>
            <Input prefix={<UserOutlined />} placeholder="पिता का नाम" maxLength={100} />
          </Item>
        </Col>

        {/* Phone + Profile Photo side by side (matches public form) */}
        <Col xs={24} sm={16}>
          <Item label="WhatsApp / Phone" name="phone_no" rules={phoneRules}>
            <Input prefix={<PhoneOutlined />} placeholder="10-digit number" maxLength={10}
              onKeyDown={e => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault(); }} />
          </Item>
        </Col>
        <Col xs={24} sm={8}>
          <Item label="प्रोफाइल फोटो" name="profile_photo">
            <AdminPhotoUpload />
          </Item>
        </Col>

        {/* Remaining personal fields */}
        <Col xs={24} sm={12}>
          <Item label="Alternate Phone (वैकल्पिक नंबर)" name="alternate_phone">
            <Input prefix={<PhoneOutlined />} placeholder="Alternate number" maxLength={10}
              onKeyDown={e => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault(); }} />
          </Item>
        </Col>
        <Col xs={24} sm={12}>
          <Item label="Date of Birth (जन्म तिथि)" name="dob">
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" placeholder="DD-MM-YYYY"
              disabledDate={d => d && d.isAfter(moment())} />
          </Item>
        </Col>
        <Col xs={24} sm={12}>
          <Item label="Gotra (गोत्र)" name="gotra">
            <Input prefix={<UserOutlined />} placeholder="गोत्र" maxLength={100} />
          </Item>
        </Col>
        <Col xs={24} sm={12}>
          <Item label="Email (ईमेल)" name="email"
            rules={[{ type: 'email', message: 'Valid email required' }]}>
            <Input prefix={<MailOutlined />} placeholder="email@example.com" maxLength={255} />
          </Item>
        </Col>
      </Row>

      {/* Business */}
      <div style={sectionStyle}>Business / Profession (व्यवसाय)</div>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Item label="Main Sector (मुख्य क्षेत्र)" name="sector">
            <Select placeholder="Select sector" showSearch optionFilterProp="children"
              onChange={val => { setSelectedSector(val); form.setFieldsValue({ sub_sector: undefined }); }}>
              {sectors.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
            </Select>
          </Item>
        </Col>
        <Col xs={24} sm={12}>
          <Item label="Sub-sector (उप-क्षेत्र)" name="sub_sector">
            <Select placeholder={selectedSector ? 'Select sub-sector' : 'Select main sector first'}
              disabled={!selectedSector} showSearch optionFilterProp="children">
              {subOptions.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
            </Select>
          </Item>
        </Col>
        <Col xs={24} sm={12}>
          <Item label="Firm / Shop Name (फर्म/दुकान)" name="firm_name"
            rules={[{ validator: validateSafe('फर्म/दुकान का नाम') }]}>
            <Input prefix={<ShopOutlined />} placeholder="फर्म / दुकान का नाम" maxLength={200} />
          </Item>
        </Col>
        <Col xs={24} sm={12}>
          <Item label="Establishment Year (स्थापना वर्ष)" name="establishment_year"
            rules={[{ type: 'number', min: 1900, max: new Date().getFullYear(), message: `Valid year` }]}>
            <InputNumber style={{ width: '100%' }} placeholder="e.g. 2010" min={1900} max={new Date().getFullYear()} />
          </Item>
        </Col>
        <Col xs={24}>
          <Item label="Business Address (व्यावसायिक पता)" name="business_address"
            rules={[{ validator: validateSafe('पता') }]}>
            <TextArea rows={2} placeholder="दुकान/ऑफिस का पूरा पता" maxLength={500} />
          </Item>
        </Col>
        <Col xs={24} sm={12}>
          <Item label="व्यवसाय का शहर (Business City)" name="current_city"
            rules={[{ required: true, message: 'व्यवसाय का शहर दर्ज करें' }]}>
            <Input prefix={<EnvironmentOutlined />} placeholder="जैसे: हैदराबाद, मुंबई, जयपुर" maxLength={100} />
          </Item>
        </Col>
        <Col xs={24} sm={12}>
          <Item label="राज्य (State)" name="state"
            rules={[{ required: true, message: 'राज्य दर्ज करें' }]}>
            <Input prefix={<GlobalOutlined />} placeholder="जैसे: राजस्थान, तेलंगाना" maxLength={100} />
          </Item>
        </Col>
        <Col xs={24}>
          <Item label="About Business (व्यवसाय के बारे में)" name="business_about"
            rules={[{ validator: validateSafe('व्यवसाय विवरण') }]}>
            <TextArea rows={3} placeholder="व्यवसाय, सेवाओं या उत्पादों के बारे में लिखें..." maxLength={600} />
          </Item>
        </Col>
        <Col xs={24} sm={8}>
          <Item label="अनुभव (वर्ष) — Experience Years" name="experience_years">
            <InputNumber min={0} max={70} placeholder="जैसे: 10" style={{ width: '100%' }} />
          </Item>
        </Col>
        <Col xs={24} sm={8}>
          <Item label="रैंक / पदवी — Rank" name="biz_rank"
            rules={[{ validator: validateSafe('Rank') }]}>
            <Input placeholder="जैसे: Gold Partner, Senior Advisor" maxLength={100} />
          </Item>
        </Col>
        <Col xs={24} sm={8}>
          <Item label="पद / Post" name="post"
            rules={[{ validator: validateSafe('Post') }]}>
            <Input placeholder="जैसे: Director, Proprietor, Manager" maxLength={100} />
          </Item>
        </Col>
        <Col xs={24}>
          <Item label="दुकान / व्यवसाय की फोटो (optional)" name="business_photo">
            <AdminMultiPhotoUpload />
          </Item>
        </Col>
      </Row>

      {/* Location */}
      <div style={sectionStyle}>Location — मूल स्थान (गाँव / जिला)</div>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={8}>
          <Item label="Village (गाँव)" name="village"
            rules={[{ required: true, message: 'गाँव दर्ज करें' }, { validator: validateName('गाँव') }]}>
            <Input prefix={<EnvironmentOutlined />} placeholder="गाँव" maxLength={100} />
          </Item>
        </Col>
        <Col xs={24} sm={8}>
          <Item label="Tehsil (तहसील)" name="tehsil"
            rules={[{ required: true, message: 'तहसील दर्ज करें' }, { validator: validateName('तहसील') }]}>
            <Input prefix={<EnvironmentOutlined />} placeholder="तहसील" maxLength={100} />
          </Item>
        </Col>
        <Col xs={24} sm={8}>
          <Item label="District (जिला)" name="district"
            rules={[{ required: true, message: 'जिला दर्ज करें' }, { validator: validateName('जिला') }]}>
            <Input prefix={<EnvironmentOutlined />} placeholder="जिला" maxLength={100} />
          </Item>
        </Col>
      </Row>

      {/* Admin */}
      <div style={sectionStyle}>Admin Fields</div>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Item label="Verification Status" name="verification_status" initialValue="Pending">
            <Select>
              <Option value="Pending">⏳ Pending</Option>
              <Option value="Verified">✅ Verified</Option>
              <Option value="Rejected">❌ Rejected</Option>
            </Select>
          </Item>
        </Col>
        <Col xs={24}>
          <Item label="Admin Notes" name="admin_notes"
            rules={[{ validator: validateSafe('Notes') }]}>
            <TextArea rows={2} placeholder="Internal notes..." maxLength={500} />
          </Item>
        </Col>
      </Row>
    </>
  );

  const sectionStyle = {
    fontSize: 11, fontWeight: 700, color: '#8c8c8c',
    textTransform: 'uppercase', letterSpacing: '1.2px',
    marginBottom: 12, marginTop: 8,
    paddingBottom: 6, borderBottom: '1px solid #f0f0f0',
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <ShopOutlined style={{ color: '#d4a84b' }} />
                <span style={{ color: 'white', fontWeight: 600, fontSize: 15 }}>Business Directory</span>
                {total > 0 && (
                  <Tag color="gold" style={{ fontSize: 11, fontWeight: 700, marginLeft: 4 }}>
                    {total} entries
                  </Tag>
                )}
              </div>
            }
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', borderRadius: '8px 8px 0 0'}}
            extra={
              <Space>
                <ShareFormButton />
                <Button icon={<PlusOutlined />} onClick={openAdd}
                  style={{ background: '#d4a84b', borderColor: '#d4a84b', color: '#fff', borderRadius: 8, fontWeight: 700 }}>
                  Add Entry
                </Button>
              </Space>
            }
          >
            <Tabs
              activeKey={activeTab}
              onChange={tab => {
                setActiveTab(tab);
                if (tab === 'update-requests') fetchUpdateRequests();
              }}
              tabBarStyle={{ padding: '0 8px', marginBottom: 0 }}
            >
              {/* ── Tab 1: Submissions ──────────────────────────────────────── */}
              <Tabs.TabPane tab="Submissions" key="submissions">
                {/* Filter bar */}
                <div style={{ padding: '10px 8px 0' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <Input
                      prefix={<SearchOutlined style={{ color: '#999' }} />}
                      placeholder="नाम, मोबाइल, फर्म, शहर से खोजें..."
                      style={{ flex: 1, minWidth: 0 }}
                      value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1); }}
                      allowClear
                    />
                    <Button
                      icon={<EnvironmentOutlined />}
                      onClick={() => setShowFilters(f => !f)}
                      style={{
                        borderColor: (filterSector || filterStatus || filterCity || filterState) ? '#d4a84b' : '#d9d9d9',
                        color:       (filterSector || filterStatus || filterCity || filterState) ? '#d4a84b' : '#666',
                        borderRadius: 8, flexShrink: 0,
                      }}>
                      Filters
                      {(filterSector || filterStatus || filterCity || filterState)
                        ? <Tag color="gold" style={{ marginLeft: 4, fontSize: 10, padding: '0 4px' }}>
                            {[filterSector, filterStatus, filterCity, filterState].filter(Boolean).length}
                          </Tag>
                        : null
                      }
                    </Button>
                  </div>
                  {showFilters && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8,
                      background: '#f8faff', borderRadius: 8, padding: '10px 12px',
                      border: '1px solid #e8eaf0' }}>
                      <Select
                        placeholder="व्यवसाय क्षेत्र"
                        style={{ minWidth: 160, flex: 1 }}
                        allowClear
                        value={filterSector || undefined}
                        onChange={val => { setFilterSector(val || ''); setPage(1); }}
                        showSearch optionFilterProp="children"
                      >
                        {sectors.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
                      </Select>
                      <Input
                        prefix={<EnvironmentOutlined style={{ color: '#999' }} />}
                        placeholder="शहर (जैसे: हैदराबाद)"
                        style={{ minWidth: 140, flex: 1 }}
                        value={filterCity}
                        onChange={e => { setFilterCity(e.target.value); setPage(1); }}
                        allowClear
                      />
                      <Input
                        prefix={<GlobalOutlined style={{ color: '#999' }} />}
                        placeholder="राज्य (जैसे: राजस्थान)"
                        style={{ minWidth: 140, flex: 1 }}
                        value={filterState}
                        onChange={e => { setFilterState(e.target.value); setPage(1); }}
                        allowClear
                      />
                      <Select
                        placeholder="स्थिति"
                        style={{ minWidth: 120, flex: 1 }}
                        allowClear
                        value={filterStatus || undefined}
                        onChange={val => { setFilterStatus(val || ''); setPage(1); }}
                      >
                        <Option value="Pending">⏳ Pending</Option>
                        <Option value="Verified">✅ Verified</Option>
                        <Option value="Rejected">❌ Rejected</Option>
                      </Select>
                      {(filterSector || filterStatus || filterCity || filterState) && (
                        <Button size="small" danger onClick={() => {
                          setFilterSector(''); setFilterStatus('');
                          setFilterCity('');   setFilterState('');
                          setPage(1);
                        }}>Clear All</Button>
                      )}
                    </div>
                  )}
                </div>
                <Table
                  columns={columns}
                  dataSource={data}
                  loading={loading}
                  scroll={{ x: 960 }}
                  style={{ padding: '0 8px 16px' }}
                  rowClassName={() => 'directory-row'}
                  onRow={record => ({
                    onClick: () => { setViewRecord(record); setViewOpen(true); },
                    style: { cursor: 'pointer' },
                  })}
                  pagination={{
                    current: page,
                    total,
                    pageSize: 20,
                    onChange: p => setPage(p),
                    showTotal: t => `Total ${t} entries`,
                    size: 'small',
                  }}
                  locale={{ emptyText: 'कोई डेटा नहीं। "Add Entry" से जोड़ें।' }}
                />
              </Tabs.TabPane>

              {/* ── Tab 2: Update Requests ──────────────────────────────────── */}
              <Tabs.TabPane
                tab={
                  <span>
                    Update Requests
                    {updateRequestCount > 0 && (
                      <Badge count={updateRequestCount} size="small"
                        style={{ marginLeft: 6, background: '#d4a84b' }} />
                    )}
                  </span>
                }
                key="update-requests"
              >
                <div style={{ padding: '8px 8px 0', marginBottom: 8, fontSize: 12, color: '#888' }}>
                  Public users ने अपनी जानकारी अपडेट करने का अनुरोध किया है।
                  Approve करने पर नई जानकारी directory में दिखेगी। Reject करने पर पुरानी जानकारी बनी रहेगी।
                </div>
                <Table
                  dataSource={updateRequests}
                  loading={loadingRequests}
                  scroll={{ x: 700 }}
                  style={{ padding: '0 8px 16px' }}
                  pagination={{ pageSize: 10, size: 'small' }}
                  locale={{ emptyText: 'कोई pending update request नहीं।' }}
                  columns={[
                    { title: '#', dataIndex: 'id', key: 'id', width: 52 },
                    {
                      title: 'Name',
                      key: 'name',
                      render: (_, r) => {
                        const nd = r.new_data || r;
                        return (
                          <div>
                            <Text strong style={{ fontSize: 13 }}>{nd.name || '—'}</Text>
                            {nd.father_name && <><br /><Text type="secondary" style={{ fontSize: 11 }}>S/o {nd.father_name}</Text></>}
                          </div>
                        );
                      },
                    },
                    {
                      title: 'Phone',
                      key: 'phone',
                      width: 120,
                      render: (_, r) => <Text style={{ fontSize: 13 }}>{(r.new_data || r).phone_no || '—'}</Text>,
                    },
                    {
                      title: 'Business',
                      key: 'biz',
                      render: (_, r) => {
                        const nd = r.new_data || r;
                        return (
                          <div>
                            {nd.firm_name && <Text style={{ fontSize: 13 }}>{nd.firm_name}</Text>}
                            {nd.current_city && <><br /><Text type="secondary" style={{ fontSize: 11 }}>{nd.current_city}{nd.state ? `, ${nd.state}` : ''}</Text></>}
                          </div>
                        );
                      },
                    },
                    {
                      title: 'Requested',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      width: 140,
                      render: v => v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—',
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: 240,
                      render: (_, record) => (
                        <Space size={6} onClick={e => e.stopPropagation()}>
                          <Button size="small"
                            onClick={() => setDiffModal(record)}
                            style={{ borderColor: '#7a0000', color: '#7a0000', borderRadius: 8, fontWeight: 600 }}>
                            View Changes
                          </Button>
                          <Button size="small"
                            loading={approving === record.id}
                            onClick={() => approveRequest(record.id)}
                            style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff', borderRadius: 8, fontWeight: 600 }}>
                            Approve
                          </Button>
                          <Popconfirm
                            title="इस अपडेट अनुरोध को रद्द करें? पुरानी जानकारी बनी रहेगी।"
                            onConfirm={() => rejectRequest(record.id)}
                            okText="हाँ, रद्द करें" cancelText="नहीं" okType="danger"
                            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}>
                            <Button size="small" danger loading={rejecting === record.id}
                              style={{ borderRadius: 8, fontWeight: 600 }}>
                              Reject
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Tabs.TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>

      {/* ── View Detail Drawer ─────────────────────────────────────────────── */}
      <Drawer
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        width={window.innerWidth <= 768 ? '100%' : 560}
        title={
          <span style={{ fontWeight: 700, color: '#7a0000', fontSize: 15 }}>
            <EyeOutlined style={{ marginRight: 8, color: '#d4a84b' }} />
            व्यवसाय विवरण
          </span>
        }
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button onClick={() => setViewOpen(false)}>बंद करें</Button>
            <Button icon={<WhatsAppOutlined />}
              style={{ background: '#25D366', borderColor: '#25D366', color: '#fff' }}
              onClick={async () => {
                if (!viewRecord) return;
                try {
                  await axios.post(`${baseUrl}/business-directory/${viewRecord.id}/send-update-link`, {}, { headers: authHeader() });
                  notification.success({ message: 'Update link sent!', description: `WhatsApp link sent to ${viewRecord.phone_no}`, placement: 'topRight' });
                } catch (err) {
                  notification.error({ message: err?.response?.data?.message || 'Failed to send link', placement: 'topRight' });
                }
              }}>
              Send Update Link
            </Button>
            <Button icon={<EditOutlined />}
              style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
              onClick={() => { setViewOpen(false); openEdit(viewRecord); }}>
              Edit
            </Button>
          </div>
        }
        destroyOnClose
      >
        {viewRecord && <DetailView r={viewRecord} />}
      </Drawer>

      {/* ── Add / Edit Drawer ───────────────────────────────────────────────── */}
      <Drawer
        title={
          <span style={{ fontWeight: 700, color: '#7a0000' }}>
            {editingId ? '✏️ Edit Entry' : '➕ Add New Entry'}
          </span>
        }
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={window.innerWidth <= 768 ? '100%' : 640}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={onSave}
              style={{ background: '#7a0000', borderColor: '#7a0000' }}>
              {editingId ? 'Update' : 'Save'}
            </Button>
          </div>
        }
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {renderFormFields()}
        </Form>
      </Drawer>

      {/* ── Diff Modal (Update Request review) ─────────────────────────────── */}
      <Modal
        open={!!diffModal}
        onCancel={() => setDiffModal(null)}
        width={760}
        centered
        destroyOnClose
        title={
          <span style={{ fontWeight: 700, color: '#7a0000', fontSize: 15 }}>
            <CheckCircleOutlined style={{ marginRight: 8, color: '#d4a84b' }} />
            Update Request — परिवर्तन देखें
          </span>
        }
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button onClick={() => setDiffModal(null)}>बंद करें</Button>
            <Popconfirm
              title="इस अपडेट अनुरोध को रद्द करें? पुरानी जानकारी बनी रहेगी।"
              onConfirm={() => rejectRequest(diffModal?.id)}
              okText="हाँ, रद्द करें" cancelText="नहीं" okType="danger"
              icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}>
              <Button danger loading={rejecting === diffModal?.id}>Reject</Button>
            </Popconfirm>
            <Button type="primary" loading={approving === diffModal?.id}
              onClick={() => approveRequest(diffModal?.id)}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}>
              Approve — नई जानकारी लागू करें
            </Button>
          </div>
        }
      >
        {diffModal && (() => {
          const oldD = diffModal.old_data || {};
          const newD = diffModal.new_data || diffModal;

          const FIELDS = [
            { label: 'नाम',           key: 'name' },
            { label: 'पिता का नाम',  key: 'father_name' },
            { label: 'मोबाइल',       key: 'phone_no' },
            { label: 'ईमेल',         key: 'email' },
            { label: 'वैकल्पिक नंबर', key: 'alternate_phone' },
            { label: 'मुख्य क्षेत्र', key: 'sector_label' },
            { label: 'उप-क्षेत्र',   key: 'sub_sector_label' },
            { label: 'फर्म / दुकान', key: 'firm_name' },
            { label: 'स्थापना वर्ष', key: 'establishment_year' },
            { label: 'व्यवसाय का शहर', key: 'current_city' },
            { label: 'राज्य',        key: 'state' },
            { label: 'व्यावसायिक पता', key: 'business_address' },
            { label: 'व्यवसाय के बारे में', key: 'business_about' },
            { label: 'गाँव',         key: 'village' },
            { label: 'तहसील',        key: 'tehsil' },
            { label: 'जिला',         key: 'district' },
            { label: 'जन्म तिथि',   key: 'dob' },
            { label: 'गोत्र',        key: 'gotra' },
            { label: 'अनुभव (वर्ष)', key: 'experience_years' },
            { label: 'रैंक / पदवी',  key: 'biz_rank' },
            { label: 'पद / Post',    key: 'post' },
          ];

          const changed = FIELDS.filter(f => String(oldD[f.key] || '') !== String(newD[f.key] || ''));

          return (
            <div>
              {/* Photos */}
              {(oldD.profile_photo || newD.profile_photo) && (
                <div style={{ display: 'flex', gap: 24, marginBottom: 16, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>पुरानी प्रोफाइल फोटो</div>
                    {oldD.profile_photo
                      ? <img src={oldD.profile_photo} alt="old" style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', border: '2px solid #e8e8e8' }} />
                      : <div style={{ width: 72, height: 72, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#bbb' }}>कोई नहीं</div>
                    }
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#52c41a', marginBottom: 4, fontWeight: 600 }}>नई प्रोफाइल फोटो</div>
                    {newD.profile_photo
                      ? <img src={newD.profile_photo} alt="new" style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', border: '2px solid #52c41a' }} />
                      : <div style={{ width: 72, height: 72, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#bbb' }}>कोई नहीं</div>
                    }
                  </div>
                </div>
              )}

              {/* Summary */}
              {changed.length > 0
                ? <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8,
                    padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#614700' }}>
                    <strong>{changed.length} field{changed.length > 1 ? 's' : ''} changed</strong> — highlighted below
                  </div>
                : <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8,
                    padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#135200' }}>
                    कोई बदलाव नहीं पाया गया (सिर्फ फोटो या अन्य fields में हो सकता है)।
                  </div>
              }

              {/* Field comparison table */}
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr',
                  background: '#7a0000', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                  <div style={{ padding: '8px 10px' }}>Field</div>
                  <div style={{ padding: '8px 10px', borderLeft: '1px solid rgba(255,255,255,0.15)' }}>पुरानी जानकारी</div>
                  <div style={{ padding: '8px 10px', borderLeft: '1px solid rgba(255,255,255,0.15)' }}>नई जानकारी</div>
                </div>
                {FIELDS.map(f => {
                  const oldVal = oldD[f.key];
                  const newVal = newD[f.key];
                  const isChanged = String(oldVal || '') !== String(newVal || '');
                  if (!oldVal && !newVal) return null;
                  return (
                    <div key={f.key}
                      style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr',
                        borderTop: '1px solid #f0f0f0',
                        background: isChanged ? '#fffbe6' : '#fff' }}>
                      <div style={{ padding: '7px 10px', fontSize: 12, color: '#888', fontWeight: 500 }}>{f.label}</div>
                      <div style={{ padding: '7px 10px', fontSize: 13, color: isChanged ? '#999' : '#333',
                        borderLeft: '1px solid #f0f0f0',
                        textDecoration: isChanged ? 'line-through' : 'none' }}>
                        {String(oldVal || '—')}
                      </div>
                      <div style={{ padding: '7px 10px', fontSize: 13,
                        color: isChanged ? '#1a5c2e' : '#333',
                        fontWeight: isChanged ? 600 : 400,
                        borderLeft: '1px solid #f0f0f0',
                        background: isChanged ? '#f6ffed' : 'transparent' }}>
                        {String(newVal || '—')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}

export default SubmissionList;
